/**
 * Payoff schedule generation and solving.
 *
 * A schedule is produced client-side (the builder needs live recalc as the user
 * drags a target date), POSTed in full, and validated server-side. Keeping
 * generation on one side avoids the class of bug where the server independently
 * recomputes and the numbers shift after saving.
 */

import { PaymentKind } from '../../types';
import {
  addMonths,
  daysInMonth,
  dueDayOccurrencesBetween,
  payDayFor,
  startOfDay,
  toDayKey,
  today as todayFn,
} from './dates';
import {
  cycleInterest,
  computeMinimumPayment,
  minimumViablePayment,
} from './minimums';

/** Hard stop for feasible plans. */
const MAX_CYCLES = 600;
/** Rows generated for a knowingly-infeasible plan (decision: warn but allow). */
const INFEASIBLE_HORIZON_CYCLES = 120;

const EPSILON = 0.005;

export type ExtraPaymentSpec =
  /** `from` is the first month the extra applies. Omitted means "from the
   *  start", which is what every extra did before the field existed. */
  | { type: 'recurring'; dayOfMonth: number; amount: number; from?: Date }
  | { type: 'oneOff'; date: Date; amount: number };

export interface PlannedPaymentDraft {
  sequence: number;
  dueDate: Date;
  kind: PaymentKind;
  amount: number;
  generatedAmount: number;
  isCustomized: boolean;
  openingBalance: number;
  interestAccrued: number;
  principalApplied: number;
  closingBalance: number;
}

export interface ScheduleInput {
  startingBalance: number;
  apr: number;
  dueDay: number;
  minFloor: number;
  principalPct: number;
  /** Balance is true as of this day; the first payment lands strictly after it. */
  originAsOf: Date;
  /** Flat monthly amount. Ignored for months present in `overrides`. */
  monthlyPayment: number;
  extras?: ExtraPaymentSpec[];
  /** Per-month overrides keyed by the payment's day key ('YYYY-MM-DD').
   *  Keyed by date rather than sequence so an override survives regeneration
   *  when the schedule length changes. */
  overrides?: Record<string, number>;
}

export interface ScheduleResult {
  payments: PlannedPaymentDraft[];
  totalPrincipal: number;
  totalInterest: number;
  /** null when the plan never closes the card. */
  payoffDate: Date | null;
  isFeasible: boolean;
  /** The regular (non-final, non-extra) amount actually used. */
  monthlyPayment: number;
  /** The last scheduled payment, typically a partial. */
  finalPayment: number | null;
  startDate: Date;
  endDate: Date;
}

/** First payment date: the first `dueDay` occurrence STRICTLY after the later
 *  of today and the balance-as-of date.
 *
 *  Strictly-after matters: the old engine had `<` in one place and `<=` in
 *  another, which produced a duplicated "Min Payment" tile whenever today
 *  happened to be a card's due day. */
export function firstPaymentDate(originAsOf: Date, dueDay: number, from?: Date): Date {
  const anchor = startOfDay(from ?? todayFn());
  const asOf = startOfDay(originAsOf);
  const base = anchor > asOf ? anchor : asOf;
  let candidate = payDayFor(base.getFullYear(), base.getMonth(), dueDay);
  if (candidate <= base) {
    candidate = payDayFor(base.getFullYear(), base.getMonth() + 1, dueDay);
  }
  return candidate;
}

/** Extra payments landing in the window (prevDue, thisDue], in date order. */
function extrasInWindow(
  extras: ExtraPaymentSpec[],
  windowStartExclusive: Date,
  windowEndInclusive: Date
): { date: Date; amount: number }[] {
  const out: { date: Date; amount: number }[] = [];
  for (const extra of extras) {
    if (extra.amount <= 0) continue;
    if (extra.type === 'oneOff') {
      const d = startOfDay(extra.date);
      if (d > windowStartExclusive && d <= windowEndInclusive) {
        out.push({ date: d, amount: extra.amount });
      }
      continue;
    }
    // Recurring: the occurrence could fall in either calendar month spanned by
    // the window, so test both. An occurrence before `from` is skipped below.
    const months = [
      { y: windowStartExclusive.getFullYear(), m: windowStartExclusive.getMonth() },
      { y: windowEndInclusive.getFullYear(), m: windowEndInclusive.getMonth() },
    ];
    const seen = new Set<string>();
    for (const { y, m } of months) {
      const day = Math.min(extra.dayOfMonth, daysInMonth(y, m));
      const d = new Date(y, m, day);
      const key = toDayKey(d);
      if (seen.has(key)) continue;
      seen.add(key);
      if (extra.from && d < startOfDay(extra.from)) continue;
      if (d > windowStartExclusive && d <= windowEndInclusive) {
        out.push({ date: d, amount: extra.amount });
      }
    }
  }
  return out.sort((a, b) => a.date.getTime() - b.date.getTime());
}

/**
 * Simulate the schedule.
 *
 * One cycle = (previous due day, this due day]. Within a cycle:
 *   1. interest accrues on the opening balance
 *   2. extras dated inside the window are applied, in date order
 *   3. the scheduled payment lands on the due day
 *
 * Cycle interest is attributed to the SCHEDULED/FINAL row, so extras read as
 * pure principal — which is what they are.
 */
export function generateSchedule(input: ScheduleInput): ScheduleResult {
  // minFloor / principalPct are part of ScheduleInput (the solvers use them)
  // but generateSchedule itself only needs the amounts it is handed.
  const {
    startingBalance,
    apr,
    dueDay,
    originAsOf,
    monthlyPayment,
    extras = [],
    overrides = {},
  } = input;

  const payments: PlannedPaymentDraft[] = [];
  let balance = startingBalance;
  let totalInterest = 0;
  let sequence = 0;

  const start = firstPaymentDate(originAsOf, dueDay);
  let dueDate = start;
  // The first cycle's window opens the day after the balance is known.
  let windowStart = startOfDay(originAsOf);

  let feasible = true;
  let cycles = 0;
  let payoffDate: Date | null = null;
  let finalPayment: number | null = null;

  while (balance > EPSILON) {
    if (cycles >= MAX_CYCLES) {
      feasible = false;
      break;
    }

    const overrideAmount = overrides[toDayKey(dueDate)];
    const regularAmount = overrideAmount !== undefined ? overrideAmount : monthlyPayment;

    // --- 1. Extras first, before interest accrues -------------------------
    // Matches cardLedger's cycle order: a payment made mid-cycle reduces the
    // balance the statement charges interest on. Extras therefore carry no
    // interest of their own and are pure principal.
    const windowExtras = extrasInWindow(extras, windowStart, dueDate);
    const extrasTotal = windowExtras.reduce((s, e) => s + e.amount, 0);

    for (const extra of windowExtras) {
      if (balance <= EPSILON) break;
      const rowOpening = balance;
      const amt = Math.min(extra.amount, Math.max(0, balance));
      if (amt <= 0) continue;
      balance = rowOpening - amt;
      payments.push({
        sequence: sequence++,
        dueDate: extra.date,
        kind: 'EXTRA',
        amount: round2(amt),
        generatedAmount: round2(amt),
        isCustomized: false,
        openingBalance: round2(rowOpening),
        interestAccrued: 0,
        principalApplied: round2(amt),
        closingBalance: round2(balance),
      });
    }

    if (balance <= EPSILON) {
      payoffDate = payments.length ? payments[payments.length - 1].dueDate : dueDate;
      finalPayment = payments.length ? payments[payments.length - 1].amount : null;
      break;
    }

    // --- 2. Interest on the post-extras balance ---------------------------
    const preInterest = balance;
    const interest = cycleInterest(preInterest, apr);
    const gross = preInterest + interest;
    totalInterest += interest;

    // A plan that can never close the card: the regular payment plus this
    // cycle's extras don't cover the interest.
    if (regularAmount + extrasTotal <= interest + EPSILON) {
      feasible = false;
      if (cycles >= INFEASIBLE_HORIZON_CYCLES) break;
    }

    // --- 3. The statement payment -----------------------------------------
    // Each row satisfies opening + interest - amount === closing, and each
    // row's opening equals the previous row's closing. The server revalidates
    // exactly this, so the two must agree.
    const isFinal = regularAmount >= gross - EPSILON;
    const amount = isFinal ? gross : regularAmount;
    balance = Math.max(0, gross - amount);

    payments.push({
      sequence: sequence++,
      dueDate,
      kind: isFinal ? 'FINAL' : 'SCHEDULED',
      amount: round2(amount),
      generatedAmount: round2(overrideAmount !== undefined ? monthlyPayment : amount),
      isCustomized: overrideAmount !== undefined,
      openingBalance: round2(preInterest),
      interestAccrued: round2(interest),
      principalApplied: round2(amount - interest),
      closingBalance: round2(balance),
    });

    if (isFinal || balance <= EPSILON) {
      payoffDate = dueDate;
      finalPayment = round2(amount);
      break;
    }

    cycles += 1;
    if (!feasible && cycles >= INFEASIBLE_HORIZON_CYCLES) break;

    windowStart = dueDate;
    dueDate = payDayFor(dueDate.getFullYear(), dueDate.getMonth() + 1, dueDay);
  }

  const totalPrincipal = payments.reduce((s, p) => s + p.principalApplied, 0);

  return {
    payments,
    totalPrincipal: round2(totalPrincipal),
    totalInterest: round2(totalInterest),
    payoffDate,
    isFeasible: feasible && payoffDate !== null,
    monthlyPayment,
    finalPayment,
    startDate: payments.length ? payments[0].dueDate : start,
    endDate: payments.length ? payments[payments.length - 1].dueDate : start,
  };
}

/** Solve for the monthly payment that closes the card by `targetDate`.
 *
 *  Bisection rather than the annuity closed form: the floor/minimum interaction
 *  and month-length clamping break the closed form, and extras make it worse.
 *  ~60 iterations over a ≤600-cycle simulation is microseconds. */
export function solveForPayment(
  input: Omit<ScheduleInput, 'monthlyPayment'>,
  targetDate: Date
): {
  monthlyPayment: number;
  result: ScheduleResult;
  /** Set when the target could not be met exactly. */
  warning?: string;
} {
  const { startingBalance, apr, dueDay, minFloor, principalPct, originAsOf } = input;
  const start = firstPaymentDate(originAsOf, dueDay);
  const target = startOfDay(targetDate);

  const cyclesAvailable = dueDayOccurrencesBetween(
    addMonths(start, -1) > startOfDay(originAsOf) ? addMonths(start, -1) : startOfDay(originAsOf),
    target,
    dueDay
  );

  const build = (payment: number) =>
    generateSchedule({ ...input, monthlyPayment: payment });

  if (cyclesAvailable < 1) {
    // Target is before the first possible payment: pay it all at once.
    const lump = startingBalance + cycleInterest(startingBalance, apr);
    const result = build(lump);
    return {
      monthlyPayment: round2(lump),
      result,
      warning: `The earliest possible payment date is ${toDayKey(start)}. This pays the card off in one payment.`,
    };
  }

  // The minimum-only schedule is the slowest legal plan. If the target is later
  // than that, no smaller payment exists — clamp up and report the real date.
  const minOnly = build(computeMinimumPayment(startingBalance, apr, minFloor, principalPct));

  let lo = minimumViablePayment(startingBalance, apr);
  let hi = startingBalance + cycleInterest(startingBalance, apr);

  for (let i = 0; i < 60; i += 1) {
    const mid = (lo + hi) / 2;
    const r = build(mid);
    if (r.payoffDate && r.payoffDate <= target) hi = mid;
    else lo = mid;
  }

  // Round UP to whole dollars — users think in dollars, and rounding down can
  // push the payoff a month past the target.
  let payment = Math.ceil(hi);

  const floorMin = computeMinimumPayment(startingBalance, apr, minFloor, principalPct);
  let warning: string | undefined;

  if (payment < floorMin) {
    payment = Math.ceil(floorMin);
    const result = build(payment);
    warning =
      minOnly.payoffDate && result.payoffDate
        ? `Your minimum payment alone pays this off by ${formatMonth(result.payoffDate)} — sooner than your target. Using the minimum.`
        : 'That target requires less than your minimum payment. Using the minimum.';
    return { monthlyPayment: payment, result, warning };
  }

  const result = build(payment);
  if (!result.payoffDate || result.payoffDate > target) {
    warning = `Could not hit ${toDayKey(target)} exactly; the closest schedule pays off ${result.payoffDate ? formatMonth(result.payoffDate) : 'never'}.`;
  }

  return { monthlyPayment: payment, result, warning };
}

/** Solve for the payoff date given a monthly payment. */
export function solveForDate(
  input: Omit<ScheduleInput, 'monthlyPayment'>,
  monthlyPayment: number
): { result: ScheduleResult; warning?: string } {
  const result = generateSchedule({ ...input, monthlyPayment });
  let warning: string | undefined;

  const floorMin = computeMinimumPayment(
    input.startingBalance,
    input.apr,
    input.minFloor,
    input.principalPct
  );
  if (monthlyPayment < floorMin - EPSILON) {
    warning = `$${Math.round(monthlyPayment).toLocaleString()} is below this card's minimum of $${Math.round(floorMin).toLocaleString()}.`;
  } else if (!result.isFeasible) {
    const viable = minimumViablePayment(input.startingBalance, input.apr);
    warning = `This payment never clears the balance — interest alone is about $${Math.round(viable).toLocaleString()}/month.`;
  }

  return { result, warning };
}

/** A single lump-sum payment. Does NOT suppress the card's minimum schedule;
 *  the engine stacks it on top. */
export function generateLumpSum(input: {
  startingBalance: number;
  apr: number;
  amount: number;
  date: Date;
}): ScheduleResult {
  const date = startOfDay(input.date);
  const amount = Math.min(input.amount, input.startingBalance);
  const closing = Math.max(0, input.startingBalance - amount);
  const payment: PlannedPaymentDraft = {
    sequence: 0,
    dueDate: date,
    kind: 'EXTRA',
    amount: round2(amount),
    generatedAmount: round2(amount),
    isCustomized: false,
    openingBalance: round2(input.startingBalance),
    interestAccrued: 0,
    principalApplied: round2(amount),
    closingBalance: round2(closing),
  };
  return {
    payments: [payment],
    totalPrincipal: round2(amount),
    totalInterest: 0,
    payoffDate: closing <= EPSILON ? date : null,
    isFeasible: true,
    monthlyPayment: 0,
    finalPayment: null,
    startDate: date,
    endDate: date,
  };
}

/**
 * Regenerate the tail of a plan after an edit, preserving already-CONFIRMED
 * payments and any other customized amounts.
 *
 * Needed because editing one month invalidates every subsequent row's
 * opening/closing balance — without this the calendar shows the card closing
 * on the wrong date.
 */
export function recalcTail(input: {
  keep: PlannedPaymentDraft[]; // confirmed / historical rows, in order
  balanceAfterKept: number;
  asOf: Date;
  apr: number;
  dueDay: number;
  minFloor: number;
  principalPct: number;
  monthlyPayment: number;
  extras?: ExtraPaymentSpec[];
  overrides?: Record<string, number>;
}): ScheduleResult {
  const tail = generateSchedule({
    startingBalance: input.balanceAfterKept,
    apr: input.apr,
    dueDay: input.dueDay,
    minFloor: input.minFloor,
    principalPct: input.principalPct,
    originAsOf: input.asOf,
    monthlyPayment: input.monthlyPayment,
    extras: input.extras,
    overrides: input.overrides,
  });

  const offset = input.keep.length;
  const payments = [
    ...input.keep,
    ...tail.payments.map(p => ({ ...p, sequence: p.sequence + offset })),
  ];

  const keptInterest = input.keep.reduce((s, p) => s + p.interestAccrued, 0);
  const keptPrincipal = input.keep.reduce((s, p) => s + p.principalApplied, 0);

  return {
    ...tail,
    payments,
    totalInterest: round2(keptInterest + tail.totalInterest),
    totalPrincipal: round2(keptPrincipal + tail.totalPrincipal),
    startDate: payments.length ? payments[0].dueDate : tail.startDate,
    endDate: payments.length ? payments[payments.length - 1].dueDate : tail.endDate,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatMonth(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}
